// When each round starts, and the calendar file that puts it in a player's phone.
//
// WHY THIS IS NOT DERIVED FROM THE TOURNAMENT'S DATES. A tournament carries a start date
// and an end date, and until now the app inferred everything else from them — the TRF
// export wrote the start date into the round-date record for every round, and the public
// page could not say when round 3 began because nothing knew. Real events are not
// regular: a weekend open plays two rounds on Saturday and one on Sunday, a club
// championship plays one round a week with a break at Easter. So the schedule is stored,
// one entry per round, and only SUGGESTED from the dates.
//
// AN ENTRY MAY BE HALF EMPTY, on purpose. "Round 5 is on the 12th, time to be confirmed"
// is a real state an organiser is in, and refusing it would push them to invent a time
// and publish it. A date with no time becomes an all-day calendar entry; a time with no
// date is kept but is not a calendar entry at all, because there is nothing to put it on.
//
// Pure: no DOM, no state, no Firestore. Formatting for the screen belongs to the UI,
// which has the translator; what comes back from here is ISO parts and an .ics file.

// How long a round is assumed to last in the calendar file, when the schedule does not
// say and nothing else can. Four hours is a classic long game plus the walk to the venue;
// the alternative — a zero-length event — shows up in a calendar as a bare pin at the
// start time and tells the player nothing about their afternoon.
export const DEFAULT_ROUND_MINUTES = 240;

const ISO_DATE = /^(\d{4})-(\d{2})-(\d{2})$/;
const CLOCK = /^(\d{1,2}):(\d{2})$/;

function cleanDate(value) {
    const s = String(value === null || value === undefined ? '' : value).trim();
    return ISO_DATE.test(s) ? s : '';
}

function cleanTime(value) {
    const s = String(value === null || value === undefined ? '' : value).trim();
    const m = CLOCK.exec(s);
    if (!m) return '';
    const h = parseInt(m[1], 10);
    const min = parseInt(m[2], 10);
    if (h > 23 || min > 59) return '';
    return String(h).padStart(2, '0') + ':' + m[2];
}

/**
 * Read anything shaped roughly like a schedule into exactly `roundCount` entries.
 *
 * Longer stored schedules are truncated and shorter ones padded, because the round count
 * is a setting an arbiter changes: a tournament cut from 9 rounds to 7 must not keep
 * publishing dates for rounds 8 and 9, and one extended to 11 must not lose the two new
 * rows to an array that is the wrong length.
 */
export function normalizeSchedule(raw, roundCount) {
    const count = Math.max(0, parseInt(roundCount, 10) || 0);
    const list = Array.isArray(raw) ? raw : [];
    return Array.from({ length: count }, (_, i) => {
        const entry = list[i] || {};
        return { date: cleanDate(entry.date), time: cleanTime(entry.time) };
    });
}

/** Is there anything worth showing? An all-blank schedule is the same as none. */
export function hasSchedule(schedule) {
    return (schedule || []).some(e => e && (e.date || e.time));
}

/** One round's entry (1-based), or a blank one. */
export function entryForRound(schedule, roundNo) {
    const i = (parseInt(roundNo, 10) || 0) - 1;
    const entry = (schedule || [])[i];
    return { date: cleanDate(entry && entry.date), time: cleanTime(entry && entry.time) };
}

function addDays(isoDate, days) {
    const m = ISO_DATE.exec(isoDate);
    if (!m) return '';
    // Built in UTC so that adding a day never lands on 23:00 the previous day in a zone
    // observing summer time — the bug that turns a Sunday round into a Saturday one.
    const d = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])));
    d.setUTCDate(d.getUTCDate() + days);
    return d.toISOString().slice(0, 10);
}

function addMinutesToClock(time, minutes) {
    const m = CLOCK.exec(time || '');
    if (!m) return '';
    const total = Number(m[1]) * 60 + Number(m[2]) + minutes;
    // Deliberately clamped rather than rolled over to the next day: a suggested second
    // round at 02:00 the following morning is never what an organiser meant, and a
    // suggestion that is visibly wrong is worse than one that stops at the end of the day.
    const capped = Math.min(total, 23 * 60 + 59);
    return String(Math.floor(capped / 60)).padStart(2, '0') + ':' + String(capped % 60).padStart(2, '0');
}

/**
 * A first draft of the schedule, for the "fill in" button.
 *
 * Plays `roundsPerDay` rounds a day from the start date, the first at the tournament's
 * start time and each later one `gapMinutes` after the previous. It is a suggestion and
 * nothing more — every field stays editable — which is why it does not try to know about
 * rest days, split sessions or the Easter break.
 *
 * @param {Object} opts { startDate, startTime, roundCount, roundsPerDay, gapMinutes }
 */
export function suggestSchedule({ startDate, startTime, roundCount, roundsPerDay, gapMinutes } = {}) {
    const count = Math.max(0, parseInt(roundCount, 10) || 0);
    const perDay = Math.max(1, parseInt(roundsPerDay, 10) || 1);
    const gap = Math.max(0, parseInt(gapMinutes, 10) || 300);
    const firstDate = cleanDate(startDate);
    const firstTime = cleanTime(startTime) || '10:00';

    return Array.from({ length: count }, (_, i) => {
        const day = Math.floor(i / perDay);
        const slot = i % perDay;
        return {
            date: firstDate ? addDays(firstDate, day) : '',
            time: slot === 0 ? firstTime : addMinutesToClock(firstTime, slot * gap)
        };
    });
}

/**
 * The next round that has not started yet, as a 1-based round number, or null.
 *
 * `now` is passed in rather than read from the clock so the caller — and the tests —
 * decide what "now" is.
 */
export function nextScheduledRound(schedule, now) {
    const list = normalizeSchedule(schedule, (schedule || []).length);
    const at = now instanceof Date ? now : new Date(now);
    if (isNaN(at.getTime())) return null;
    for (let i = 0; i < list.length; i++) {
        const start = localStart(list[i]);
        if (start && start.getTime() > at.getTime()) return i + 1;
    }
    return null;
}

/**
 * An entry as a local Date, or null when there is no date.
 *
 * LOCAL, not UTC. A round starts at ten o'clock in the playing hall whatever the reader's
 * device thinks; building it in UTC would move every round by the viewer's offset.
 */
function localStart(entry) {
    const date = cleanDate(entry && entry.date);
    if (!date) return null;
    const m = ISO_DATE.exec(date);
    const time = cleanTime(entry && entry.time);
    const clock = time ? CLOCK.exec(time) : null;
    return new Date(
        Number(m[1]), Number(m[2]) - 1, Number(m[3]),
        clock ? Number(clock[1]) : 0,
        clock ? Number(clock[2]) : 0
    );
}

// ---- calendar file ---------------------------------------------------------

function icsEscape(text) {
    return String(text === null || text === undefined ? '' : text)
        .replace(/\\/g, '\\\\')
        .replace(/;/g, '\\;')
        .replace(/,/g, '\\,')
        .replace(/\r?\n/g, '\\n');
}

/**
 * Fold a content line to the 75-octet limit RFC 5545 sets, continuing with a leading
 * space. Folded conservatively by character rather than by octet: a Norwegian tournament
 * name is full of two-byte characters, and a line that is legal by characters and illegal
 * by octets is rejected by exactly the strict parsers this is folded for.
 */
function fold(line) {
    const limit = 70;
    if (line.length <= limit) return line;
    const parts = [line.slice(0, limit)];
    let rest = line.slice(limit);
    while (rest.length > limit - 1) {
        parts.push(' ' + rest.slice(0, limit - 1));
        rest = rest.slice(limit - 1);
    }
    if (rest) parts.push(' ' + rest);
    return parts.join('\r\n');
}

function stampUtc(date) {
    const pad = n => String(n).padStart(2, '0');
    return date.getUTCFullYear() + pad(date.getUTCMonth() + 1) + pad(date.getUTCDate())
        + 'T' + pad(date.getUTCHours()) + pad(date.getUTCMinutes()) + pad(date.getUTCSeconds()) + 'Z';
}

function stampFloating(date) {
    const pad = n => String(n).padStart(2, '0');
    return date.getFullYear() + pad(date.getMonth() + 1) + pad(date.getDate())
        + 'T' + pad(date.getHours()) + pad(date.getMinutes()) + '00';
}

/**
 * The schedule as an .ics file.
 *
 * TIMES ARE FLOATING (no timezone, no Z). A round at 18:00 is at 18:00 where it is
 * played, and a floating time is the one form every calendar shows unchanged wherever the
 * reader happens to be. The alternative — converting to UTC — would be right only for
 * readers in the organiser's own zone, and quietly wrong for everyone travelling, which
 * is a good part of who downloads this.
 *
 * A round with a date but no time becomes an all-day entry.
 *
 * @param {Object} opts
 * @param {string} opts.name       the tournament's name
 * @param {string} [opts.location]
 * @param {string} opts.uidBase    something stable and unique per tournament (its id)
 * @param {Array}  opts.schedule
 * @param {Date}   [opts.now]      the DTSTAMP; injectable so tests are deterministic
 * @param {number} [opts.roundMinutes]
 * @param {Function} [opts.roundLabel] (n) => string, so the caller supplies the translation
 * @returns {string} the file's contents, CRLF throughout
 */
export function toIcs({ name, location, uidBase, schedule, now, roundMinutes, roundLabel } = {}) {
    const stamp = stampUtc(now instanceof Date ? now : new Date());
    const minutes = Math.max(1, parseInt(roundMinutes, 10) || DEFAULT_ROUND_MINUTES);
    const label = typeof roundLabel === 'function' ? roundLabel : (n => 'Round ' + n);
    const base = String(uidBase || 'tournament').replace(/[^A-Za-z0-9_-]/g, '');

    const lines = [
        'BEGIN:VCALENDAR',
        'VERSION:2.0',
        'PRODID:-//Zugzwang//Tournament schedule//EN',
        'CALSCALE:GREGORIAN',
        'METHOD:PUBLISH',
        fold('X-WR-CALNAME:' + icsEscape(name || 'Tournament'))
    ];

    (schedule || []).forEach((entry, i) => {
        const start = localStart(entry);
        if (!start) return;                       // nothing to put it on; see the header
        const roundNo = i + 1;
        const allDay = !cleanTime(entry && entry.time);

        lines.push('BEGIN:VEVENT');
        lines.push(`UID:round-${roundNo}-${base}@zugzwang.no`);
        lines.push('DTSTAMP:' + stamp);
        if (allDay) {
            const day = cleanDate(entry.date).replace(/-/g, '');
            lines.push('DTSTART;VALUE=DATE:' + day);
            lines.push('DTEND;VALUE=DATE:' + addDays(cleanDate(entry.date), 1).replace(/-/g, ''));
        } else {
            const end = new Date(start.getTime() + minutes * 60000);
            lines.push('DTSTART:' + stampFloating(start));
            lines.push('DTEND:' + stampFloating(end));
        }
        lines.push(fold('SUMMARY:' + icsEscape(`${label(roundNo)} – ${name || ''}`.trim())));
        if (location) lines.push(fold('LOCATION:' + icsEscape(location)));
        lines.push('END:VEVENT');
    });

    lines.push('END:VCALENDAR');
    return lines.join('\r\n') + '\r\n';
}
